/**
 * Benefits → Dashboard (admin).
 *
 * Single-page operational view: active period progress, plan
 * breakdown, who hasn't enrolled yet, monthly cost rollups,
 * pending life events to review.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Tabs, TabPanel } from "@/components/ui/Tabs";
import { BenefitsAnalyticsTab } from "@/pages/benefits/BenefitsAnalyticsTab";
import { ClaimsAnalyticsTab } from "@/pages/benefits/ClaimsAnalyticsTab";
import {
  AlertTriangle,
  Bell,
  CalendarDays,
  Clock,
  DollarSign,
  Download,
  FileCode,
  FileSpreadsheet,
  FileText,
  Loader2,
  Lock,
  Send,
  ShieldCheck,
  Users,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import {
  download834Export,
  downloadEnrollmentExport,
  getBenefitsDashboard,
  listExportFormats,
  preview834Export,
  sendEnrollmentReminders,
  type BenefitsDashboard,
  type DashboardNotEnrolled,
  type Edi834PreviewResponse,
  type Edi834Request,
  type ExportFileType,
  type ExportFormat,
} from "@/api/benefits";

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  });
}

export function BenefitsDashboardPage() {
  const { addToast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [tab, setTab] = useState<"overview" | "analytics" | "claims">(
    "overview",
  );
  const [data, setData] = useState<BenefitsDashboard | null>(null);
  // Per-employee + bulk "Sending…" state so the row + bulk button can
  // disable independently and the user can fire them in parallel.
  const [remindingIds, setRemindingIds] = useState<Set<string>>(new Set());
  const [remindingAll, setRemindingAll] = useState(false);

  // ── Export-enrollment modal state ─────────────────────────────
  const [showExport, setShowExport] = useState(false);
  const [exportFormats, setExportFormats] = useState<ExportFormat[]>([]);
  const [exportFormat, setExportFormat] = useState<string>("generic");
  const [exportFileType, setExportFileType] = useState<ExportFileType>("csv");
  const [exportFullSsn, setExportFullSsn] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ── 834 EDI modal state ────────────────────────────────────────
  const [show834, setShow834] = useState(false);
  const [edi834, setEdi834] = useState<Edi834Request>({
    sender_id: "",
    receiver_id: "",
    group_number: "",
    sponsor_name: "",
    payer_name: "",
    usage_indicator: "T",
    carrier_filter: "",
  });
  const [edi834Preview, setEdi834Preview] = useState<Edi834PreviewResponse | null>(
    null,
  );
  const [edi834PreviewLoading, setEdi834PreviewLoading] = useState(false);
  const [edi834Downloading, setEdi834Downloading] = useState(false);
  const [edi834FullSsn, setEdi834FullSsn] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const d = await getBenefitsDashboard();
      setData(d);
    } catch {
      setData({
        period: null,
        monthly_employer_cost: 0,
        monthly_employee_cost: 0,
        pending_life_events: 0,
      });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleRemindOne = async (emp: DashboardNotEnrolled) => {
    setRemindingIds((s) => new Set(s).add(emp.id));
    try {
      const res = await sendEnrollmentReminders([emp.id]);
      if (res.sent > 0) {
        addToast(
          "success",
          `Reminder sent to ${emp.name || emp.email || "employee"}`,
        );
      } else if (res.failures > 0) {
        addToast("error", "Couldn't deliver reminder email — check logs");
      } else {
        addToast("info", "Employee was already enrolled — list refreshed");
      }
      refresh();
    } catch {
      addToast("error", "Failed to send reminder");
    } finally {
      setRemindingIds((s) => {
        const n = new Set(s);
        n.delete(emp.id);
        return n;
      });
    }
  };

  // Lazy-load the format list the first time the dialog opens so the
  // dashboard's initial paint isn't slowed down by a request the user
  // may never trigger.
  const openExportDialog = async () => {
    setShowExport(true);
    if (exportFormats.length === 0) {
      try {
        const fmts = await listExportFormats();
        setExportFormats(fmts);
      } catch {
        // Fall back to a hardcoded list — the backend's the source of
        // truth but we shouldn't block the dialog if the format-list
        // endpoint blips.
        setExportFormats([
          { key: "generic", label: "Generic CSV (works with any carrier)" },
          { key: "blue_cross", label: "Blue Cross / Blue Shield" },
          { key: "delta_dental", label: "Delta Dental" },
          { key: "anthem", label: "Anthem" },
          { key: "aetna", label: "Aetna" },
          { key: "uhc", label: "UnitedHealthcare" },
        ]);
      }
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await downloadEnrollmentExport({
        format: exportFormat,
        file_type: exportFileType,
        full_ssn: exportFullSsn && isSuperAdmin,
      });
      const ssnLabel = res.ssnMode === "full" ? "full SSN" : "SSN masked";
      const rowsLabel = res.rowCount === 1 ? "1 enrollment" : `${res.rowCount} enrollments`;
      if (res.rowCount === 0) {
        addToast(
          "warning",
          "Exported file is empty — no enrollments in the active period yet.",
        );
      } else {
        addToast("success", `Exported ${rowsLabel} (${ssnLabel})`);
      }
      setShowExport(false);
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        e?.response?.data?.detail ||
          "Failed to export enrollment file",
      );
    } finally {
      setExporting(false);
    }
  };

  // ── 834 modal handlers ─────────────────────────────────────────

  const buildEdi834Payload = (): Edi834Request => {
    // Strip empty-string optional fields so the backend can fill in
    // its placeholders cleanly (the SPA shows the user it'll do so).
    const out: Edi834Request = {
      usage_indicator: edi834.usage_indicator,
    };
    if (edi834.sender_id?.trim()) out.sender_id = edi834.sender_id.trim();
    if (edi834.receiver_id?.trim()) out.receiver_id = edi834.receiver_id.trim();
    if (edi834.group_number?.trim()) out.group_number = edi834.group_number.trim();
    if (edi834.sponsor_name?.trim()) out.sponsor_name = edi834.sponsor_name.trim();
    if (edi834.payer_name?.trim()) out.payer_name = edi834.payer_name.trim();
    if (edi834.carrier_filter?.trim())
      out.carrier_filter = edi834.carrier_filter.trim();
    if (edi834FullSsn && isSuperAdmin) out.full_ssn = true;
    return out;
  };

  const handleOpen834 = () => {
    setShow834(true);
    setEdi834Preview(null);
    setEdi834FullSsn(false);
  };

  const handlePreview834 = async () => {
    setEdi834PreviewLoading(true);
    try {
      const res = await preview834Export(buildEdi834Payload());
      setEdi834Preview(res);
      if (res.total_carriers === 0) {
        addToast(
          "warning",
          "No carriers matched. Remove the filter or check the active period.",
        );
      }
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        e?.response?.data?.detail || "Failed to preview 834 bundle",
      );
    } finally {
      setEdi834PreviewLoading(false);
    }
  };

  const handleDownload834 = async () => {
    setEdi834Downloading(true);
    try {
      const res = await download834Export(buildEdi834Payload());
      const label = res.carrierCount === 1
        ? "Downloaded 834 file"
        : `Downloaded ZIP bundle (${res.carrierCount} carriers)`;
      const tone = res.warningCount > 0 || res.placeholderCount > 0
        ? "warning"
        : "success";
      addToast(
        tone,
        `${label} — ${res.memberCount} members${
          res.warningCount > 0 ? `, ${res.warningCount} warnings` : ""
        }${
          res.placeholderCount > 0
            ? `; ${res.placeholderCount} placeholder field${res.placeholderCount === 1 ? "" : "s"}, review before sending`
            : ""
        }`,
      );
      setShow834(false);
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        e?.response?.data?.detail || "Failed to generate 834 file",
      );
    } finally {
      setEdi834Downloading(false);
    }
  };

  const handleRemindAll = async () => {
    if (!data?.period) return;
    if (
      !confirm(
        `Send a reminder to all ${data.period.not_elected_count} ` +
          `employees who haven't enrolled yet?`,
      )
    ) {
      return;
    }
    setRemindingAll(true);
    try {
      const res = await sendEnrollmentReminders();
      const msg =
        res.failures > 0
          ? `Sent ${res.sent} (${res.failures} failed)`
          : `Sent ${res.sent} reminder${res.sent === 1 ? "" : "s"}`;
      addToast(res.failures > 0 ? "warning" : "success", msg);
      refresh();
    } catch {
      addToast("error", "Failed to send bulk reminders");
    } finally {
      setRemindingAll(false);
    }
  };

  if (!data) {
    return (
      <Card>
        <div className="py-10 text-center text-text-muted">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </div>
      </Card>
    );
  }

  const period = data.period;
  const enrolledPct =
    period && period.eligible_count > 0
      ? Math.round((period.enrolled_count / period.eligible_count) * 100)
      : 0;

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Benefits Dashboard"
        subtitle="Enrollment progress, cost rollups, and life events to review"
        actions={
          tab === "overview" ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={openExportDialog}>
                <Download className="h-4 w-4" />
                Export enrollment
              </Button>
              <Button variant="secondary" onClick={handleOpen834}>
                <FileCode className="h-4 w-4" />
                Generate 834
              </Button>
            </div>
          ) : undefined
        }
      />

      <Tabs
        tabs={[
          { id: "overview", label: "Overview" },
          { id: "analytics", label: "Analytics" },
          { id: "claims", label: "Claims Analytics" },
        ]}
        activeTab={tab}
        onChange={(id) =>
          setTab(id as "overview" | "analytics" | "claims")
        }
        aria-label="Benefits views"
      />

      <TabPanel id="analytics" activeTab={tab}>
        <BenefitsAnalyticsTab />
      </TabPanel>

      <TabPanel id="claims" activeTab={tab}>
        <ClaimsAnalyticsTab />
      </TabPanel>

      <TabPanel id="overview" activeTab={tab}>
      {/* Top-line enrollment stats when a period is active — matches
          the spec from step 8 of the CBIZ wishlist. */}
      {period && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Tile
            icon={<Users className="h-4 w-4" />}
            label="Total eligible"
            value={period.eligible_count}
          />
          <Tile
            icon={<ShieldCheck className="h-4 w-4 text-success" />}
            label="Enrolled"
            value={`${period.enrolled_count} (${enrolledPct}%)`}
            tone="success"
          />
          <Tile
            icon={<DollarSign className="h-4 w-4 text-text-muted" />}
            label="Waived"
            value={`${period.waived_count} (${
              period.eligible_count > 0
                ? Math.round(
                    (period.waived_count / period.eligible_count) * 100,
                  )
                : 0
            }%)`}
          />
          <Tile
            icon={<AlertTriangle className="h-4 w-4 text-warning" />}
            label="Not yet elected"
            value={`${period.not_elected_count} (${
              period.eligible_count > 0
                ? Math.round(
                    (period.not_elected_count / period.eligible_count) * 100,
                  )
                : 0
            }%)`}
            tone={period.not_elected_count > 0 ? "warning" : undefined}
          />
          <Tile
            icon={<Clock className="h-4 w-4" />}
            label="Days remaining"
            value={
              period.days_remaining !== null
                ? `${period.days_remaining}`
                : "—"
            }
            tone={
              period.days_remaining !== null && period.days_remaining <= 7
                ? "warning"
                : undefined
            }
          />
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <Tile
          icon={<DollarSign className="h-4 w-4" />}
          label="Employer / month"
          value={fmt(data.monthly_employer_cost)}
        />
        <Tile
          icon={<DollarSign className="h-4 w-4" />}
          label="Employee / month"
          value={fmt(data.monthly_employee_cost)}
        />
        <Tile
          icon={<AlertTriangle className="h-4 w-4 text-warning" />}
          label="Pending life events"
          value={data.pending_life_events}
          tone={data.pending_life_events > 0 ? "warning" : undefined}
        />
      </div>

      {/* Active period block */}
      {period ? (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-accent" />
                Active enrollment period
              </span>
            </CardTitle>
            <Badge variant="success">{period.status}</Badge>
          </CardHeader>
          <div>
            <p className="text-sm text-text-primary">{period.name}</p>
            <p className="text-xs text-text-muted mt-0.5">
              Closes {period.end_date}
              {period.days_remaining !== null && (
                <> · {period.days_remaining} days remaining</>
              )}
            </p>
            <div className="mt-3">
              <div className="text-xs text-text-muted mb-1">
                {period.enrolled_count} of {period.eligible_count} employees
                enrolled
              </div>
              <div className="h-2 rounded-full bg-surface-hover/50 overflow-hidden">
                <div
                  className="h-full bg-accent"
                  style={{ width: `${enrolledPct}%` }}
                />
              </div>
            </div>
          </div>

          {period.plan_breakdown.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wide text-text-muted mb-1.5">
                Plan breakdown
              </p>
              <ul className="space-y-1.5">
                {period.plan_breakdown.map((p) => {
                  const total = period.plan_breakdown.reduce(
                    (sum, x) => sum + x.count,
                    0,
                  );
                  const pct = total > 0 ? Math.round((p.count / total) * 100) : 0;
                  return (
                    <li
                      key={p.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <span className="w-40 truncate text-text-primary">
                        {p.name}
                      </span>
                      <div className="flex-1 h-2 rounded bg-surface-hover/40 overflow-hidden">
                        <div
                          className="h-full bg-accent/70 rounded"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-16 text-right text-xs tabular-nums text-text-muted">
                        {p.count} · {pct}%
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {period.not_enrolled.length > 0 && (
            <div className="mt-5 border-t border-border/30 pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wide text-text-muted">
                  Not enrolled yet ({period.not_elected_count})
                </p>
                <Button
                  variant="secondary"
                  onClick={handleRemindAll}
                  isLoading={remindingAll}
                  disabled={period.not_elected_count === 0}
                >
                  <Bell className="h-4 w-4" />
                  Remind all
                </Button>
              </div>
              <ul className="divide-y divide-border/30">
                {period.not_enrolled.map((e) => {
                  const sending = remindingIds.has(e.id);
                  return (
                    <li
                      key={e.id}
                      className="py-2 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <Link
                          to={`/employees/${e.id}`}
                          className="text-sm font-medium text-text-primary hover:text-accent hover:underline"
                        >
                          {e.name || "Unnamed employee"}
                        </Link>
                        {e.email && (
                          <p className="text-xs text-text-muted truncate">
                            {e.email}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemindOne(e)}
                        disabled={sending || !e.email}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text-primary hover:bg-accent/5 hover:border-accent/40 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={
                          e.email
                            ? "Send a reminder email"
                            : "No email on file — can't send"
                        }
                      >
                        {sending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3" />
                        )}
                        Send reminder
                      </button>
                    </li>
                  );
                })}
              </ul>
              {period.not_elected_count > period.not_enrolled.length && (
                <p className="text-xs text-text-muted mt-2">
                  Showing first {period.not_enrolled.length} of{" "}
                  {period.not_elected_count} — use &quot;Remind all&quot; to
                  reach everyone.
                </p>
              )}
            </div>
          )}
        </Card>
      ) : (
        <Card className="mb-4">
          <div className="py-6 text-center text-sm">
            <Clock className="h-6 w-6 mx-auto mb-2 text-text-muted opacity-60" />
            <p className="text-text-primary font-medium">
              No active enrollment period
            </p>
            <p className="text-text-muted mt-1">
              Create one in{" "}
              <Link
                to="/benefits/periods"
                className="text-accent hover:underline"
              >
                Enrollment Periods
              </Link>
              .
            </p>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <Users className="h-4 w-4 text-accent" />
              Quick links
            </span>
          </CardTitle>
        </CardHeader>
        <ul className="divide-y divide-border/30 text-sm">
          <li className="py-2">
            <Link to="/benefits/plans" className="text-accent hover:underline">
              Configure benefit plans →
            </Link>
          </li>
          <li className="py-2">
            <Link
              to="/benefits/life-events"
              className="text-accent hover:underline"
            >
              Review life events →
            </Link>
          </li>
          <li className="py-2">
            <Link
              to="/benefits/payroll"
              className="text-accent hover:underline"
            >
              Export payroll deductions →
            </Link>
          </li>
          <li className="py-2">
            <Link to="/cobra" className="text-accent hover:underline">
              COBRA admin →
            </Link>
          </li>
          <li className="py-2">
            <Link
              to="/benefits/invoices"
              className="text-accent hover:underline"
            >
              Carrier invoice reconciliation →
            </Link>
          </li>
        </ul>
      </Card>

      {/* ── Export enrollment modal ─────────────────────────────────
          This is the carrier-portal upload file. The defaults (generic
          CSV, SSN masked) are the safest choice; specific carrier
          formats + full-SSN toggle are below the fold so HR doesn't
          accidentally export sensitive PII on the first try. */}
      <Modal
        isOpen={showExport}
        onClose={() => !exporting && setShowExport(false)}
        title="Export enrollment"
        size="lg"
      >
        <div className="space-y-5">
          <p className="text-sm text-text-muted">
            Generate the file you'll upload to the carrier portal. Each
            format matches what that carrier expects — column order,
            headers, action codes, all of it.
          </p>

          {/* Carrier / format picker */}
          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-text-primary mb-2">
              Carrier format
            </legend>
            {exportFormats.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-text-muted py-3">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading formats…
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {exportFormats.map((f) => {
                  const selected = exportFormat === f.key;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setExportFormat(f.key)}
                      disabled={exporting}
                      className={`text-left rounded-lg border px-3 py-2.5 text-sm transition-all ${
                        selected
                          ? "border-accent bg-accent/5 ring-1 ring-accent"
                          : "border-border bg-surface hover:border-accent/40"
                      }`}
                    >
                      <span className="font-medium text-text-primary">
                        {f.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </fieldset>

          {/* File-type toggle */}
          <fieldset>
            <legend className="text-sm font-semibold text-text-primary mb-2">
              File format
            </legend>
            <div className="inline-flex rounded-lg border border-border bg-surface p-0.5">
              <button
                type="button"
                onClick={() => setExportFileType("csv")}
                disabled={exporting}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${
                  exportFileType === "csv"
                    ? "bg-accent text-white"
                    : "text-text-muted hover:text-text-primary"
                }`}
              >
                <FileText className="h-4 w-4" />
                CSV
              </button>
              <button
                type="button"
                onClick={() => setExportFileType("xlsx")}
                disabled={exporting}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${
                  exportFileType === "xlsx"
                    ? "bg-accent text-white"
                    : "text-text-muted hover:text-text-primary"
                }`}
              >
                <FileSpreadsheet className="h-4 w-4" />
                XLSX
              </button>
            </div>
          </fieldset>

          {/* SSN visibility — gated to SUPER_ADMIN. HR_ADMIN sees the
              toggle disabled so they understand the option exists but
              isn't theirs to flip. */}
          <fieldset className="rounded-lg border border-border bg-surface-2/40 p-3">
            <legend className="px-1 text-sm font-semibold text-text-primary inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-text-muted" />
              SSN visibility
            </legend>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={exportFullSsn}
                onChange={(e) => setExportFullSsn(e.target.checked)}
                disabled={!isSuperAdmin || exporting}
                className="mt-1 rounded border-border text-accent focus:ring-accent/50"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-text-primary">
                  Include full SSN (decrypted)
                </p>
                <p className="text-xs text-text-muted mt-0.5">
                  {isSuperAdmin
                    ? "Only check this if your carrier specifically requires the full Social Security Number. Otherwise the export uses the masked last-4 (e.g. ***-**-1234)."
                    : "Super-admin only. HR admins always get the masked last-4 — request a super-admin to run full-SSN exports."}
                </p>
              </div>
            </label>
          </fieldset>

          <div className="flex justify-end gap-3 pt-2 border-t border-border">
            <Button
              variant="secondary"
              onClick={() => setShowExport(false)}
              disabled={exporting}
            >
              Cancel
            </Button>
            <Button onClick={handleExport} isLoading={exporting}>
              <Download className="h-4 w-4" />
              Download {exportFileType.toUpperCase()}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── 834 EDI generator modal ──────────────────────────────
          Two-step flow: user fills in the envelope IDs + scope, hits
          Preview to see which carriers + member counts will land in
          the bundle, then commits to Generate. This is the same shape
          Employee Navigator / bswift / Benefitfocus use to ship to
          carrier portals + SFTP. */}
      <Modal
        isOpen={show834}
        onClose={() => !edi834Downloading && setShow834(false)}
        title="Generate 834 EDI"
        size="lg"
      >
        <div className="space-y-5">
          <div className="rounded-lg border border-accent/20 bg-accent/[0.04] p-3 text-xs text-text-muted">
            <p className="text-text-primary font-medium mb-1">
              What is this?
            </p>
            <p>
              ASC X12N 834 is the HIPAA-mandated benefit enrollment
              transaction set. Every major carrier (Blue Cross, Delta
              Dental, Aetna, UHC, Anthem) accepts it at their portal
              or SFTP. One file per carrier per enrollment period —
              the broker downloads the bundle and ships each file.
            </p>
          </div>

          {/* Envelope IDs */}
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-text-primary mb-1">
              Trading partner IDs
              <span className="ml-2 text-xs font-normal text-text-muted">
                (from the carrier's setup paperwork)
              </span>
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Sender ID (ISA06)"
                placeholder="LENAVIO"
                value={edi834.sender_id ?? ""}
                onChange={(e) =>
                  setEdi834((s) => ({ ...s, sender_id: e.target.value }))
                }
                maxLength={15}
              />
              <Input
                label="Receiver ID (ISA08)"
                placeholder="BLUECROSS"
                value={edi834.receiver_id ?? ""}
                onChange={(e) =>
                  setEdi834((s) => ({ ...s, receiver_id: e.target.value }))
                }
                maxLength={15}
              />
              <Input
                label="Group # (REF*38)"
                placeholder="GRP-12345"
                value={edi834.group_number ?? ""}
                onChange={(e) =>
                  setEdi834((s) => ({ ...s, group_number: e.target.value }))
                }
                maxLength={30}
              />
              <Input
                label="Sponsor name (employer)"
                placeholder="Acme Co"
                value={edi834.sponsor_name ?? ""}
                onChange={(e) =>
                  setEdi834((s) => ({ ...s, sponsor_name: e.target.value }))
                }
                maxLength={60}
              />
            </div>
          </fieldset>

          {/* Scope + mode */}
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-text-primary mb-1">
              Scope
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Carrier filter (optional)"
                placeholder="Leave blank for all carriers"
                value={edi834.carrier_filter ?? ""}
                onChange={(e) =>
                  setEdi834((s) => ({
                    ...s,
                    carrier_filter: e.target.value,
                  }))
                }
              />
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Mode
                </label>
                <div className="inline-flex rounded-lg border border-border bg-surface p-0.5">
                  <button
                    type="button"
                    onClick={() =>
                      setEdi834((s) => ({ ...s, usage_indicator: "T" }))
                    }
                    disabled={edi834Downloading}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                      edi834.usage_indicator === "T"
                        ? "bg-accent text-white"
                        : "text-text-muted hover:text-text-primary"
                    }`}
                  >
                    Test
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setEdi834((s) => ({ ...s, usage_indicator: "P" }))
                    }
                    disabled={edi834Downloading}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                      edi834.usage_indicator === "P"
                        ? "bg-accent text-white"
                        : "text-text-muted hover:text-text-primary"
                    }`}
                  >
                    Production
                  </button>
                </div>
                <p className="text-[11px] text-text-muted mt-1">
                  Use Test until the carrier confirms a successful ingest.
                </p>
              </div>
            </div>
          </fieldset>

          {/* SSN visibility */}
          <fieldset className="rounded-lg border border-border bg-surface-2/40 p-3">
            <legend className="px-1 text-sm font-semibold text-text-primary inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-text-muted" />
              SSN in NM1*IL
            </legend>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={edi834FullSsn}
                onChange={(e) => setEdi834FullSsn(e.target.checked)}
                disabled={!isSuperAdmin || edi834Downloading}
                className="mt-1 rounded border-border text-accent focus:ring-accent/50"
              />
              <div className="flex-1 text-xs text-text-muted">
                <span className="text-sm font-medium text-text-primary block">
                  Include full SSN (qualifier 34)
                </span>
                {isSuperAdmin
                  ? "Carriers usually require the full 9-digit SSN in the NM1*IL identifier. Leave unchecked to send the employee ID under qualifier ZZ instead — useful for test runs."
                  : "Super-admin only. The file will use the employee ID under qualifier ZZ when generated by an HR admin."}
              </div>
            </label>
          </fieldset>

          {/* Preview output */}
          {edi834Preview && (
            <div className="rounded-lg border border-border bg-surface-2/30 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-text-primary">
                  Bundle preview
                </p>
                <p className="text-xs text-text-muted">
                  {edi834Preview.total_carriers} carrier
                  {edi834Preview.total_carriers === 1 ? "" : "s"} ·{" "}
                  {edi834Preview.total_members} member
                  {edi834Preview.total_members === 1 ? "" : "s"}
                </p>
              </div>
              {edi834Preview.files.length === 0 ? (
                <p className="text-sm text-text-muted py-2 text-center">
                  No matching elections found.
                </p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {edi834Preview.files.map((f) => (
                    <li
                      key={f.filename}
                      className="flex items-center justify-between gap-3 py-1"
                    >
                      <span className="text-text-primary">
                        {f.carrier_name}
                      </span>
                      <span className="text-xs text-text-muted">
                        {f.member_count} member
                        {f.member_count === 1 ? "" : "s"}
                        {f.warning_count > 0 && (
                          <span className="text-warning ml-2">
                            · {f.warning_count} warning
                            {f.warning_count === 1 ? "" : "s"}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {(edi834Preview.uses_placeholders.sender_id ||
                edi834Preview.uses_placeholders.receiver_id ||
                edi834Preview.uses_placeholders.group_number) && (
                <p className="text-xs text-warning mt-2 inline-flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Placeholder values will be used for missing IDs. The
                  carrier will reject the file unless you fill them in
                  before sending.
                </p>
              )}
            </div>
          )}

          <div className="flex justify-between gap-3 pt-2 border-t border-border">
            <Button
              variant="secondary"
              onClick={() => setShow834(false)}
              disabled={edi834Downloading}
            >
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={handlePreview834}
                isLoading={edi834PreviewLoading}
                disabled={edi834Downloading}
              >
                Preview bundle
              </Button>
              <Button
                onClick={handleDownload834}
                isLoading={edi834Downloading}
              >
                <Download className="h-4 w-4" />
                Generate 834
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      </TabPanel>
    </motion.div>
  );
}

function Tile({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  tone?: "warning" | "success";
}) {
  const cls =
    tone === "warning"
      ? "border-warning/30 bg-warning/5"
      : tone === "success"
        ? "border-success/30 bg-success/5"
        : "border-border/40 bg-surface/60";
  return (
    <div className={`rounded-md border ${cls} p-3`}>
      <div className="flex items-center gap-1.5 text-xs text-text-muted">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-1 text-xl font-semibold text-text-primary">
        {value}
      </div>
    </div>
  );
}

export default BenefitsDashboardPage;
